import { Header, notify, StatsCard } from "@general-backoffice/core";
import React, { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { type EmailLog, emailLogsEndpoints, type EmailStats, type StrapiId } from "../../../../api";

interface Filters {
  trigger: string;
  status: string;
  recipient: string;
  startDate: string;
  endDate: string;
  isTest: string;
}

interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

const TRIGGER_OPTIONS = [
  { value: "", label: "All Triggers" },
  { value: "welcome", label: "Welcome" },
  { value: "email_confirmation", label: "Email Confirmation" },
  { value: "manual_payment_invoice", label: "Manual Payment Invoice" },
  { value: "payment_confirmation", label: "Payment Confirmation" },
  { value: "payout_success", label: "Payout Success" },
  { value: "charge_success", label: "Charge Success" },
  { value: "charge_failure", label: "Charge Failure" },
  { value: "subscription_failure", label: "Subscription Failure" },
  { value: "subscription_failure_escalated", label: "Subscription Escalated (5+ failures)" },
  { value: "bot_deactivation", label: "Bot Deactivation" },
  { value: "partner_warning", label: "Partner Warning" },
  { value: "partner_blocked", label: "Partner Blocked" },
  { value: "drawdown_20", label: "Drawdown 20%" },
  { value: "newsletter", label: "Newsletter" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "sent", label: "Sent" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
];

const TEST_OPTIONS = [
  { value: "", label: "All Emails" },
  { value: "true", label: "Test Only" },
  { value: "false", label: "Real Only" },
];

const STATUS_COLORS: Record<string, string> = {
  sent: "success",
  pending: "warning",
  failed: "danger",
};

const EmailLogs: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "emailLogs" });

  // Stats state
  const [stats, setStats] = useState<EmailStats>({
    total: 0,
    byStatus: { sent: 0, failed: 0, pending: 0 },
    byTrigger: {},
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Logs state
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters state
  const [filters, setFilters] = useState<Filters>({
    trigger: "",
    status: "",
    recipient: "",
    startDate: "",
    endDate: "",
    isTest: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0,
    pageCount: 0,
  });

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  // Resend state
  const [isResending, setIsResending] = useState<StrapiId | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setIsLoadingStats(true);
      const { data } = await emailLogsEndpoints.getStats({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setStats(data || { total: 0, byStatus: { sent: 0, failed: 0, pending: 0 }, byTrigger: {} });
    } catch (err: unknown) {
      console.error("Failed to load stats:", err);
    } finally {
      setIsLoadingStats(false);
    }
  }, [filters.startDate, filters.endDate]);

  const loadLogs = useCallback(async () => {
    try {
      setIsLoadingLogs(true);
      setError(null);

      const { data, meta } = await emailLogsEndpoints.getList({
        pagination: {
          page: pagination.page,
          pageSize: pagination.pageSize,
        },
        trigger: filters.trigger || undefined,
        status: filters.status || undefined,
        recipient: filters.recipient || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        isTest: filters.isTest || undefined,
      });

      setLogs(data || []);
      setPagination((prev) => ({
        ...prev,
        total: meta?.pagination?.total || 0,
        pageCount: meta?.pagination?.pageCount || Math.ceil((meta?.pagination?.total || 0) / prev.pageSize),
      }));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load logs";
      setError(errorMessage);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [filters, pagination.page, pagination.pageSize]);

  // Load data on mount and filter change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClearFilters = () => {
    setFilters({
      trigger: "",
      status: "",
      recipient: "",
      startDate: "",
      endDate: "",
      isTest: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const openViewModal = async (logId: StrapiId) => {
    try {
      const response = await emailLogsEndpoints.getOne(logId);
      setSelectedLog(response.data);
      setViewModalOpen(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load email details";
      notify.error(errorMessage);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedLog(null);
  };

  const handleResend = async (logId: StrapiId) => {
    try {
      setIsResending(logId);
      await emailLogsEndpoints.resend(logId);
      notify.success(t("resendSuccess") || "Email resent successfully");
      await loadLogs();
      await loadStats();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to resend email";
      notify.error(errorMessage);
    } finally {
      setIsResending(null);
    }
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Generate pagination items
  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    const endPage = Math.min(pagination.pageCount, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={pagination.page === 1}>
        <PaginationLink previous onClick={() => handlePageChange(pagination.page - 1)} />
      </PaginationItem>
    );

    // First page
    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis1" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={i === pagination.page}>
          <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < pagination.pageCount) {
      if (endPage < pagination.pageCount - 1) {
        items.push(
          <PaginationItem key="ellipsis2" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={pagination.pageCount}>
          <PaginationLink onClick={() => handlePageChange(pagination.pageCount)}>{pagination.pageCount}</PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={pagination.page === pagination.pageCount}>
        <PaginationLink next onClick={() => handlePageChange(pagination.page + 1)} />
      </PaginationItem>
    );

    return items;
  };

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">{t("title") || "Email Logs"}</h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        {/* Stats Cards */}
        <Row className="mb-4">
          <Col xs={12} md={6} xl={3}>
            <StatsCard.Number
              color="default"
              isDark={true}
              number={isLoadingStats ? 0 : stats.total}
              suffix=""
              title={t("totalEmails") || "Total Emails"}
              icon="fas fa-envelope"
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <StatsCard.Number
              color="success"
              isDark={true}
              number={isLoadingStats ? 0 : stats.byStatus.sent}
              suffix=""
              title={t("sentEmails") || "Sent"}
              icon="fas fa-check-circle"
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <StatsCard.Number
              color="warning"
              isDark={true}
              number={isLoadingStats ? 0 : stats.byStatus.pending}
              suffix=""
              title={t("pendingEmails") || "Pending"}
              icon="fas fa-clock"
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <StatsCard.Number
              color="danger"
              isDark={true}
              number={isLoadingStats ? 0 : stats.byStatus.failed}
              suffix=""
              title={t("failedEmails") || "Failed"}
              icon="fas fa-times-circle"
            />
          </Col>
        </Row>

        {error && (
          <Alert color="danger" className="mb-4">
            {error}
            <button type="button" className="close" onClick={() => setError(null)}>
              <span>&times;</span>
            </button>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <CardHeader>
            <h4 className="mb-0">
              <i className="fas fa-filter mr-2"></i>
              {t("filters") || "Filters"}
            </h4>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={2}>
                <FormGroup>
                  <Label for="email-logs-field-1" className="form-control-label">{t("trigger") || "Trigger"}</Label>
                  <Input id="email-logs-field-1" type="select" name="trigger" value={filters.trigger} onChange={handleFilterChange}>
                    {TRIGGER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="email-logs-field-2" className="form-control-label">{t("status") || "Status"}</Label>
                  <Input id="email-logs-field-2" type="select" name="status" value={filters.status} onChange={handleFilterChange}>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="email-logs-field-3" className="form-control-label">{t("type") || "Type"}</Label>
                  <Input id="email-logs-field-3" type="select" name="isTest" value={filters.isTest} onChange={handleFilterChange}>
                    {TEST_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="email-logs-field-4" className="form-control-label">{t("recipient") || "Recipient"}</Label>
                  <Input id="email-logs-field-4"
                    type="text"
                    name="recipient"
                    value={filters.recipient}
                    onChange={handleFilterChange}
                    placeholder={t("searchByEmail")}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="email-logs-field-5" className="form-control-label">{t("startDate") || "Start Date"}</Label>
                  <Input id="email-logs-field-5" type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="email-logs-field-6" className="form-control-label">{t("endDate") || "End Date"}</Label>
                  <Input id="email-logs-field-6" type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
                </FormGroup>
              </Col>
            </Row>
            <div className="text-right">
              <Button color="secondary" size="sm" onClick={handleClearFilters}>
                <i className="fas fa-times mr-1"></i>
                {t("clearFilters") || "Clear Filters"}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">{t("logsList") || "Email Logs"}</h3>
            <div>
              <span className="text-muted mr-3">
                {t("showing") || "Showing"} {logs.length} {t("of") || "of"} {pagination.total}
              </span>
              <Button color="secondary" size="sm" onClick={loadLogs} disabled={isLoadingLogs}>
                <i className="fas fa-sync-alt mr-1"></i>
                {t("refresh") || "Refresh"}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {isLoadingLogs ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3">{t("loading") || "Loading logs..."}</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-3x mb-3"></i>
                <p>{t("noLogs") || "No email logs found"}</p>
              </div>
            ) : (
              <>
                <Table responsive hover>
                  <thead className="thead-light">
                    <tr>
                      <th>{t("date") || "Date"}</th>
                      <th>{t("trigger") || "Trigger"}</th>
                      <th>{t("recipient") || "Recipient"}</th>
                      <th>{t("subject") || "Subject"}</th>
                      <th>{t("status") || "Status"}</th>
                      <th className="text-right">{t("actions") || "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td className="text-nowrap">{formatDate(log.createdAt)}</td>
                        <td>
                          <code className="small">{log.trigger}</code>
                          {log.isTest && (
                            <Badge color="info" className="ml-2">
                              TEST
                            </Badge>
                          )}
                        </td>
                        <td>{log.recipient}</td>
                        <td style={{ maxWidth: "300px" }} className="text-truncate">
                          {log.subject}
                        </td>
                        <td>
                          <Badge color={STATUS_COLORS[log.status ?? ""] || "secondary"}>{log.status}</Badge>
                          {log.status === "failed" && log.errorMessage && (
                            <small className="d-block text-danger mt-1" title={log.errorMessage}>
                              {log.errorMessage.substring(0, 50)}...
                            </small>
                          )}
                        </td>
                        <td className="text-right text-nowrap">
                          <Button
                            color="link"
                            size="sm"
                            onClick={() => openViewModal(log.id)}
                            title={t("view") || "View"}
                          >
                            <i className="fas fa-eye"></i>
                          </Button>
                          {log.status === "failed" && (
                            <Button
                              color="link"
                              size="sm"
                              className="text-warning"
                              onClick={() => handleResend(log.id)}
                              disabled={isResending === log.id}
                              title={t("resend") || "Resend"}
                            >
                              {isResending === log.id ? <Spinner size="sm" /> : <i className="fas fa-redo"></i>}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                {pagination.pageCount > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <Pagination>{renderPagination()}</Pagination>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* View Modal */}
      <Modal isOpen={viewModalOpen} toggle={closeViewModal} size="lg">
        <ModalHeader toggle={closeViewModal}>{t("emailDetails") || "Email Details"}</ModalHeader>
        <ModalBody>
          {selectedLog && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>{t("trigger") || "Trigger"}:</strong> <code>{selectedLog.trigger}</code>
                  {selectedLog.isTest && (
                    <Badge color="info" className="ml-2">
                      TEST
                    </Badge>
                  )}
                </Col>
                <Col md={6}>
                  <strong>{t("status") || "Status"}:</strong>{" "}
                  <Badge color={STATUS_COLORS[selectedLog.status ?? ""] || "secondary"}>{selectedLog.status}</Badge>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>{t("recipient") || "Recipient"}:</strong> {selectedLog.recipient}
                </Col>
                <Col md={6}>
                  <strong>{t("date") || "Date"}:</strong> {formatDate(selectedLog.createdAt)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>{t("subject") || "Subject"}:</strong> {selectedLog.subject}
                </Col>
              </Row>
              {selectedLog.errorMessage && (
                <Alert color="danger" className="mb-3">
                  <strong>{t("error") || "Error"}:</strong> {selectedLog.errorMessage}
                </Alert>
              )}
              <div className="mb-3">
                <strong>{t("emailContent") || "Email Content"}:</strong>
              </div>
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <iframe
                  srcDoc={selectedLog.bodyHtml ?? ""}
                  style={{
                    width: "100%",
                    height: "400px",
                    border: "none",
                  }}
                  title="Email Content"
                />
              </div>
              {selectedLog.variables && Object.keys(selectedLog.variables).length > 0 && (
                <div className="mt-3">
                  <strong>{t("variables") || "Variables Used"}:</strong>
                  <pre className="bg-light p-2 mt-2 rounded small">
                    {JSON.stringify(selectedLog.variables, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedLog?.status === "failed" && (
            <Button
              color="warning"
              onClick={() => {
                handleResend(selectedLog.id);
                closeViewModal();
              }}
            >
              <i className="fas fa-redo mr-2"></i>
              {t("resend") || "Resend"}
            </Button>
          )}
          <Button color="secondary" onClick={closeViewModal}>
            {t("close") || "Close"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default EmailLogs;
